/**
 * Application Laure Rey-Bellet
 * Navigation spatiale avec View Transitions API natives
 */

"use strict";

import "./styles/main.css";

// Initialisation de l'application
document.addEventListener("DOMContentLoaded", () => {
  // Navigation clavier et tactile
  setupKeyboardNavigation();
  setupTouchNavigation();
  
  // Focus management
  const firstFocusable = document.querySelector(
    'main button, main [href], main input, main select, main textarea, main [tabindex]:not([tabindex="-1"])'
  );
  if (firstFocusable) {
    try { firstFocusable.focus({ preventScroll: true }); } catch (_) { /* no-op */ }
  }
});

/**
 * Configuration de la navigation clavier
 */
function setupKeyboardNavigation() {
  document.addEventListener("keydown", (e) => {
    const keyMap = { 
      ArrowLeft: "left", 
      ArrowRight: "right", 
      ArrowUp: "up", 
      ArrowDown: "down" 
    };
    
    const dir = keyMap[e.key];
    if (!dir) return;
    
    // Chercher le lien correspondant sur la page actuelle
    const link = findLinkByDirection(dir);
    if (link) {
      e.preventDefault();
      // Définir la direction pour la transition
      sessionStorage.setItem('vt-dir', dir);
      // Naviguer
      link.click();
    }
  });
}

/**
 * Trouve le lien correspondant à une direction selon la page actuelle
 */
function findLinkByDirection(dir) {
  const currentPath = window.location.pathname;
  
  // Page d'accueil : toutes les directions disponibles
  if (currentPath === '/' || currentPath === '/index.html' || currentPath === '/index') {
    switch (dir) {
      case 'up': 
        return document.querySelector('a[href="/lrb.html"]') || document.querySelector('a[href="/lrb"]') || document.querySelector('a[href*="/lrb"]');
      case 'right': 
        return document.querySelector('a[href="/concepts.html"]') || document.querySelector('a[href="/concepts"]') || document.querySelector('a[href*="/concepts"]');
      case 'left': 
        return document.querySelector('a[href="/contact.html"]') || document.querySelector('a[href="/contact"]') || document.querySelector('a[href*="/contact"]');
      default: return null;
    }
  }
  
  // Pages secondaires : seulement retour à l'accueil
  // (Direction opposée à celle qui nous a amenés ici)
  if ((currentPath === '/lrb.html' || currentPath === '/lrb') && dir === 'down') {
    return document.querySelector('a[href="/index.html"]') || document.querySelector('a[href="/"]') || document.querySelector('a[href*="/index"]');
  }
  if ((currentPath === '/concepts.html' || currentPath === '/concepts') && dir === 'left') {
    return document.querySelector('a[href="/index.html"]') || document.querySelector('a[href="/"]') || document.querySelector('a[href*="/index"]');
  }
  if ((currentPath === '/contact.html' || currentPath === '/contact') && dir === 'right') {
    return document.querySelector('a[href="/index.html"]') || document.querySelector('a[href="/"]') || document.querySelector('a[href*="/index"]');
  }
  
  return null;
}

/**
 * Configuration de la navigation tactile (swipe mobile)
 * Solution optimisée pour iOS Safari et pages avec éléments interactifs
 */
function setupTouchNavigation() {
  let startX = null;
  let startY = null;
  let startTime = null;
  let isDragging = false;
  
  // Détection iOS Safari
  const isIOSSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && /Safari/.test(navigator.userAgent);
  
  // Sélecteurs d'éléments à ignorer pour les swipes
  const ignoredSelectors = 'input:not([readonly]), textarea:not([readonly]), button:not([disabled]), a, select, [contenteditable="true"]';
  
  // Pour iOS, on écoute sur les éléments spécifiques qui peuvent être problématiques
  const targetElements = isIOSSafari 
    ? [document.body, document.documentElement, document.querySelector('main')]
    : [document];
  
  targetElements.forEach(target => {
    if (!target) return;
    
    target.addEventListener("touchstart", (e) => {
      // Ignorer si on touche directement un élément interactif
      if (e.target.matches && e.target.matches(ignoredSelectors)) {
        return;
      }
      if (e.target.closest && e.target.closest(ignoredSelectors)) {
        return;
      }
      
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      isDragging = false;
      
      
      // Pour iOS, on force la "clickabilité" de l'élément
      if (isIOSSafari && target.style) {
        target.style.cursor = 'pointer';
      }
    }, { passive: true });

    target.addEventListener("touchmove", (e) => {
      if (startX === null || startY === null) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - startX);
      const deltaY = Math.abs(touch.clientY - startY);
      
      // Si on a bougé de plus de 10px, on considère que c'est un drag
      if (deltaX > 10 || deltaY > 10) {
        isDragging = true;
        // Prévenir le scroll natif pour les gestes intentionnels
        if (deltaX > 30 || deltaY > 30) {
          e.preventDefault();
        }
      }
    }, { passive: false });

    target.addEventListener("touchend", (e) => {
      if (startX === null || startY === null || startTime === null) return;
      
      // Ignorer si on finit sur un élément interactif
      if (e.target.matches && e.target.matches(ignoredSelectors)) {
        resetTouchState();
        return;
      }
      if (e.target.closest && e.target.closest(ignoredSelectors)) {
        resetTouchState();
        return;
      }
      
      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = touch.clientY - startY;
      const deltaTime = Date.now() - startTime;
      
      
      // Critères plus permissifs pour iOS
      const minDistance = isIOSSafari ? 40 : 50;
      const maxTime = isIOSSafari ? 1000 : 800;
      
      // Ignorer les swipes trop lents, trop courts, ou si on n'a pas fait de mouvement
      if (deltaTime > maxTime || !isDragging || (Math.abs(deltaX) < minDistance && Math.abs(deltaY) < minDistance)) {
        resetTouchState();
        return;
      }
      
      // Déterminer la direction principale
      const isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
      let swipeDir = null;
      
      if (isHorizontal) {
        swipeDir = deltaX > 0 ? "right" : "left";
      } else {
        swipeDir = deltaY > 0 ? "down" : "up";
      }
      
      
      // Logique inverse du swipe
      const navDir = swipeDir === "right" ? "left" : 
                    swipeDir === "left" ? "right" : 
                    swipeDir === "up" ? "down" : 
                    swipeDir === "down" ? "up" : null;
      
      if (navDir) {
        const link = findLinkByDirection(navDir);
        
        if (link) {
          e.preventDefault();
          e.stopPropagation();
          
          // Définir la direction pour la transition
          sessionStorage.setItem('vt-dir', navDir);
          
          // Navigation avec délai pour iOS Safari
          if (isIOSSafari) {
            setTimeout(() => {
              link.click();
            }, 50);
          } else {
            link.click();
          }
        }
      }
      
      resetTouchState();
    }, { passive: false });
    
    // Gestion des interruptions de touch
    target.addEventListener("touchcancel", resetTouchState, { passive: true });
  });
  
  function resetTouchState() {
    startX = startY = startTime = null;
    isDragging = false;
    
    // Nettoyer le style iOS si nécessaire
    if (isIOSSafari) {
      targetElements.forEach(target => {
        if (target && target.style) {
          target.style.cursor = '';
        }
      });
    }
  }
}